import {
  PROXY,
  YES_NO,
  applicantSection,
  otherAttachments,
  penaltyDeclaration,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import {
  EbiCondition,
  EEbiFormCategory,
  EEbiFormOffice,
  IEbiField,
  IEbiFormAttachment,
  IEbiFormDefinition,
  IEbiFormSection,
} from "modules/ebiForms/typings";

// Wspólne bloki pakietu „mieszkania komunalne i dodatki mieszkaniowe” (A10, A11, A12, B12, C8, E1–E4): pełnomocnik bez
// opłaty skarbowej, gospodarstwo domowe, deklaracja o dochodach i oświadczenia z art. 21b ustawy o ochronie praw
// lokatorów oraz pełne definicje procedur prowadzonych przez różne organy (B12/C8 — dodatek mieszkaniowy, A10/E1 — najem
// lokalu z zasobu gminy).

// ── pełnomocnik bez opłaty skarbowej ─────────────────────────────────────────

/**
 * Pełnomocnik w sprawie zwolnionej z opłaty skarbowej także w części pełnomocnictwa (art. 2 ust. 1 pkt 1 ustawy o opłacie
 * skarbowej: m.in. lit. c — świadczenia socjalne, lit. h — sprawy z ustawy o gospodarce nieruchomościami) — bez pola
 * o zwolnieniu rodzinnym, które ma sens tylko przy opłacie 17 zł.
 */
export const representationWithoutFeeSection = (): IEbiFormSection => {
  const section = representationSection();

  return { ...section, fields: section.fields.filter((field) => field.code !== "pelnomocnictwoZwolnione") };
};

/** Samo pełnomocnictwo (bez dowodu opłaty 17 zł) — `hint` wyjaśnia, dlaczego opłata nie jest należna. */
export const proxyWithoutFeeAttachment = (hint: string): IEbiFormAttachment => ({ ...proxyAttachments()[0], hint });

/** Dowód opłaty za pełnomocnictwo tylko wtedy, gdy opłata jest należna w danym wariancie sprawy (`when`). */
export const proxyFeeAttachmentWhen = (when: EbiCondition): IEbiFormAttachment => ({
  ...proxyAttachments()[1],
  visibleWhen: { all: [PROXY, { not: { field: "pelnomocnictwoZwolnione", equals: true } }, when] },
});

// ── gospodarstwo domowe ──────────────────────────────────────────────────────

export const HOUSEHOLD_MULTI: EbiCondition = { field: "gospodarstwo", equals: "wieloosobowe" };

/** Inne pełnoletnie osoby w gospodarstwie — ich oświadczeń EBI nie podpisze, więc dołącza się skany. */
export const OTHER_ADULTS: EbiCondition = { all: [HOUSEHOLD_MULTI, { field: "innePelnoletnie", equals: "tak" }] };

interface IHouseholdOptions {
  title?: string;
  description?: string;
  membersLabel?: string;
  /** Pole z liczbą osób w gospodarstwie (gdy wymaga go wzór wniosku). */
  count?: boolean;
  /** Pytanie o inne osoby pełnoletnie (ich oświadczenia — załącznik `otherAdultsAttachment`). */
  adultsQuestion?: boolean;
}

/** Skład gospodarstwa domowego: jednoosobowe albo lista pozostałych osób (imię i nazwisko, data urodzenia, pokrewieństwo). */
export const householdSection = (options: IHouseholdOptions = {}): IEbiFormSection => ({
  code: "gospodarstwoDomowe",
  title: options.title ?? "Gospodarstwo domowe",
  description: options.description,
  fields: [
    {
      code: "gospodarstwo",
      type: "radio",
      label: "Gospodarstwo domowe",
      required: true,
      options: [
        { value: "jednoosobowe", label: "jednoosobowe" },
        { value: "wieloosobowe", label: "wieloosobowe — wspólnie ze mną zamieszkują i gospodarują inne osoby" },
      ],
    },
    ...(options.count
      ? [
          {
            code: "liczbaOsob",
            type: "number",
            label: "Liczba osób wchodzących w skład gospodarstwa domowego",
            hint: "Według stanu na dzień składania wniosku, łącznie z wnioskodawcą.",
            required: true,
            min: 1,
            max: 30,
            width: "third",
          } satisfies IEbiField,
        ]
      : []),
    {
      code: "czlonkowie",
      type: "repeat",
      label: options.membersLabel ?? "Członkowie gospodarstwa domowego (poza wnioskodawcą)",
      itemLabel: "Osoba",
      required: true,
      visibleWhen: HOUSEHOLD_MULTI,
      min: 1,
      max: 20,
      fields: [
        { code: "imieNazwisko", type: "text", label: "Imię i nazwisko", required: true, width: "half", maxLength: 200 },
        { code: "dataUrodzenia", type: "date", label: "Data urodzenia", required: true, width: "half" },
        { code: "pokrewienstwo", type: "text", label: "Stopień pokrewieństwa", required: true, width: "half", maxLength: 100, placeholder: "np. żona, syn" },
      ],
    },
    ...(options.adultsQuestion
      ? [
          {
            code: "innePelnoletnie",
            type: "radio",
            label: "Czy wśród tych osób są osoby pełnoletnie?",
            required: true,
            visibleWhen: HOUSEHOLD_MULTI,
            options: YES_NO,
          } satisfies IEbiField,
        ]
      : []),
  ],
});

// ── deklaracja o dochodach i oświadczenia ────────────────────────────────────

/**
 * Deklaracja o dochodach gospodarstwa domowego za 3 pełne miesiące — układ jak propozycja wzoru MRiT dla gmin (art. 7
 * ust. 1d ustawy o dodatkach mieszkaniowych); według niej składa się też deklarację z art. 21b ust. 2 ustawy o ochronie
 * praw lokatorów. `extra` — pola dopisywane na końcu (pouczenia, klauzula).
 */
export const incomeDeclarationSection = (extra: IEbiField[] = []): IEbiFormSection => ({
  code: "deklaracja",
  title: "Deklaracja o dochodach gospodarstwa domowego",
  description:
    "Za okres pełnych trzech miesięcy kalendarzowych poprzedzających datę złożenia wniosku. Wymień osobno każde źródło dochodu każdej osoby, także swoje.",
  fields: [
    { code: "okresDochodow", type: "text", label: "Okres, za który deklaruję dochody", required: true, maxLength: 100, placeholder: "np. lipiec–wrzesień 2026 r." },
    {
      code: "dochody",
      type: "repeat",
      label: "Dochody moje i członków mojego gospodarstwa domowego w podanym okresie",
      itemLabel: "Dochód",
      required: true,
      min: 1,
      max: 40,
      fields: [
        { code: "osoba", type: "text", label: "Imię i nazwisko", required: true, width: "half", maxLength: 200 },
        { code: "miejscePracy", type: "text", label: "Miejsce pracy lub nauki", width: "half", maxLength: 200 },
        { code: "zrodlo", type: "text", label: "Źródło dochodu", required: true, width: "half", maxLength: 200, placeholder: "np. wynagrodzenie, emerytura, zasiłek" },
        { code: "kwota", type: "number", label: "Wysokość dochodu [zł]", required: true, width: "half", decimals: 2, min: 0 },
      ],
    },
    { code: "dochodRazem", type: "number", label: "Razem dochody gospodarstwa domowego [zł]", required: true, width: "half", decimals: 2, min: 0 },
    {
      code: "dochodSredni",
      type: "number",
      label: "Średni miesięczny dochód na jednego członka gospodarstwa domowego [zł]",
      required: true,
      width: "half",
      decimals: 2,
      min: 0,
    },
    ...extra,
  ],
});

const OTHER_DWELLING: EbiCondition = { field: "tytulInnyLokal", equals: "posiadam" };

/**
 * Deklaracja o dochodach, oświadczenie o stanie majątkowym i oświadczenie o nieposiadaniu tytułu prawnego do innego
 * lokalu — art. 21b ust. 1 ustawy o ochronie praw lokatorów, składane pod rygorem odpowiedzialności karnej (ust. 5).
 * Układ oświadczenia majątkowego jak wzór Rudy Śląskiej (załącznik nr 2 do wniosku 6.1).
 */
export const tenantDeclarationSections = (): IEbiFormSection[] => [
  incomeDeclarationSection(),
  {
    code: "majatek",
    title: "Oświadczenie o stanie majątkowym",
    description:
      "Majątek objęty wspólnością ustawową oraz majątek osobisty członków gospodarstwa domowego. Wpisz „brak”, jeśli dana pozycja nie dotyczy.",
    fields: [
      {
        code: "majatekNieruchomosci",
        type: "textarea",
        label: "Nieruchomości — mieszkanie, dom, gospodarstwo rolne, inne nieruchomości (powierzchnia, tytuł prawny)",
        required: true,
        maxLength: 2000,
      },
      {
        code: "majatekRuchomosci",
        type: "textarea",
        label: "Składniki mienia ruchomego — pojazdy mechaniczne, maszyny, inne przedmioty wartościowe (rodzaj, rok produkcji, wartość szacunkowa)",
        required: true,
        maxLength: 2000,
      },
      {
        code: "majatekPieniadze",
        type: "textarea",
        label: "Zasoby pieniężne — środki pieniężne w walucie polskiej i obcej, papiery wartościowe (wartość szacunkowa)",
        required: true,
        maxLength: 2000,
      },
      { code: "majatekInne", type: "textarea", label: "Inne dodatkowe informacje o stanie majątkowym", maxLength: 2000 },
    ],
  },
  {
    code: "oswiadczenia",
    title: "Oświadczenie o nieposiadaniu tytułu prawnego do innego lokalu",
    description:
      "Deklarację o dochodach i oświadczenia składasz pod rygorem odpowiedzialności karnej za złożenie fałszywego oświadczenia (art. 21b ustawy o ochronie praw lokatorów).",
    fields: [
      {
        code: "tytulInnyLokal",
        type: "radio",
        label: "Oświadczam, że ja i osoby zgłoszone we wniosku",
        required: true,
        options: [
          { value: "nieposiadam", label: "nie posiadamy tytułu prawnego do innego lokalu mieszkalnego położonego w tej samej lub pobliskiej miejscowości" },
          { value: "posiadam", label: "posiadamy tytuł prawny do innego lokalu mieszkalnego położonego w tej samej lub pobliskiej miejscowości" },
        ],
      },
      {
        code: "tytulInnyLokalOpis",
        type: "textarea",
        label: "Lokal, do którego przysługuje tytuł prawny (adres, rodzaj tytułu, osoba uprawniona)",
        required: true,
        visibleWhen: OTHER_DWELLING,
        maxLength: 1000,
      },
      penaltyDeclaration(),
    ],
  },
];

/** Oświadczenia z art. 21b pozostałych pełnoletnich osób (EBI podpisuje tylko wnioskodawca). */
export const otherAdultsAttachment = (): IEbiFormAttachment => ({
  code: "oswiadczeniaPelnoletnich",
  label: "Deklaracje i oświadczenia pozostałych pełnoletnich osób zgłoszonych we wniosku",
  hint: "Skan deklaracji o dochodach, oświadczenia o stanie majątkowym i oświadczenia o nieposiadaniu tytułu prawnego do innego lokalu, podpisanych przez każdą z tych osób.",
  visibleWhen: OTHER_ADULTS,
  required: true,
  multiple: true,
});

/** Dokumenty dochodowe gospodarstwa domowego za 3 miesiące. */
const incomeDocumentsAttachment = (hint: string): IEbiFormAttachment => ({
  code: "dochodyDokumenty",
  label: "Dokumenty potwierdzające dochody członków gospodarstwa domowego za 3 miesiące",
  hint,
  required: true,
  multiple: true,
});

// ── dodatek mieszkaniowy (B12 — urząd gminy, C8 — ośrodek / centrum świadczeń) ─

const HOUSE: EbiCondition = { field: "tytulPrawny", equals: "domJednorodzinny" };
const PART_RENTED: EbiCondition = { field: "tytulPrawny", in: ["najem", "podnajem"] };
const DISABLED: EbiCondition = { field: "niepelnosprawni", equals: "tak" };
const WHEELCHAIR_OR_ROOM: EbiCondition = { all: [DISABLED, { field: "wozekPokoj", equals: "tak" }] };
const NO_INSTALLATION: EbiCondition = {
  any: [
    { field: "centralneOgrzewanie", equals: "brak" },
    { field: "cieplaWoda", equals: "brak" },
    { field: "gazPrzewodowy", equals: "brak" },
  ],
};
const INSTALLED = [
  { value: "jest", label: "jest" },
  { value: "brak", label: "brak" },
];

interface IHousingAllowanceOptions {
  code: string;
  office: EEbiFormOffice;
  organ: string;
  description: string;
  /** Sekcja informacyjna na początku formularza (C8: zawieszony dodatek energetyczny). */
  notice?: IEbiFormSection;
}

/**
 * Wniosek o przyznanie dodatku mieszkaniowego — art. 7 ust. 1–2a ustawy z 21.06.2001 r. o dodatkach mieszkaniowych.
 * Wzór wniosku i deklaracji ustala rada gminy (art. 7 ust. 1e); układ pól (pkt 1–10 i deklaracja) jak propozycja wzorów
 * MRiT dla gmin (https://www.gov.pl/attachment/93559078-ec23-476c-9823-c562bf6c6d2c — wniosek, .doc, i
 * https://www.gov.pl/attachment/5d944f1c-327d-4cc9-bc05-c43f64a0e24b — deklaracja; oba pobrane 22.10.2026) i druk
 * Poznańskiego Centrum Świadczeń (https://bip.poznan.pl/bip/druki.html?co=show&dr_id=2264&type=pdf, pobrany 22.10.2026),
 * pomijając część „wypełnia urząd”. Potwierdzenie zarządcy (art. 7 ust. 1b) — skan podpisanego potwierdzenia jako
 * załącznik. Oświadczenie o stanie majątkowym składa się dopiero na żądanie przy wywiadzie (art. 7 ust. 4) — nie ma go
 * we wniosku. Progi dochodowe z komunikatu Prezesa GUS z 9.02.2026 r. — do aktualizacji co roku (nowa wersja definicji).
 */
export const housingAllowanceForm = ({ code, office, organ, description, notice }: IHousingAllowanceOptions): IEbiFormDefinition => ({
  code,
  version: 1,
  title: "Wniosek o przyznanie dodatku mieszkaniowego",
  category: EEbiFormCategory.Housing,
  office,
  description,
  keywords: ["dodatek mieszkaniowy", "czynsz", "opłaty za mieszkanie", "deklaracja o dochodach", "pomoc mieszkaniowa"],
  organ,
  legalBasis:
    "art. 7 ustawy z dnia 21 czerwca 2001 r. o dodatkach mieszkaniowych; uchwała rady gminy w sprawie wzoru wniosku o przyznanie dodatku mieszkaniowego i wzoru deklaracji o dochodach gospodarstwa domowego (art. 7 ust. 1e)",
  request: "Wnoszę o przyznanie dodatku mieszkaniowego na zajmowany lokal mieszkalny na zasadach ustawy z dnia 21 czerwca 2001 r. o dodatkach mieszkaniowych.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty — sprawa świadczeń socjalnych zwolniona z opłaty skarbowej, także w części pełnomocnictwa (art. 2 ust. 1 pkt 1 lit. c ustawy o opłacie skarbowej).",
  deadlineLabel: "do miesiąca",
  deadline: "Do miesiąca, w sprawie szczególnie skomplikowanej do 2 miesięcy (art. 35 § 3 KPA).",
  sections: [
    ...(notice ? [notice] : []),
    applicantSection("1–2. Wnioskodawca i adres zamieszkania"),
    representationWithoutFeeSection(),
    {
      code: "zarzadca",
      title: "3. Zarządca budynku albo inna osoba uprawniona do pobierania należności za lokal",
      visibleWhen: { not: HOUSE },
      fields: [
        { code: "zarzadcaNazwa", type: "text", label: "Nazwa zarządcy albo imię i nazwisko osoby uprawnionej", required: true, maxLength: 300 },
        { code: "zarzadcaAdres", type: "text", label: "Adres — ulica, numer domu i lokalu", required: true, maxLength: 200 },
        { code: "zarzadcaKodPocztowy", type: "postalCode", label: "Kod pocztowy", required: true, width: "third", placeholder: "00-000" },
        { code: "zarzadcaMiejscowosc", type: "text", label: "Miejscowość", required: true, width: "twoThirds", maxLength: 100 },
      ],
    },
    {
      code: "tytul",
      title: "4. Tytuł prawny do zajmowanego lokalu mieszkalnego",
      fields: [
        {
          code: "tytulPrawny",
          type: "radio",
          label: "Tytuł prawny",
          required: true,
          options: [
            { value: "najem", label: "najem" },
            { value: "podnajem", label: "podnajem" },
            { value: "spoldzielcze", label: "spółdzielcze prawo do lokalu (lokatorskie lub własnościowe)" },
            { value: "wlasnoscSpoldzielnia", label: "własność lokalu w spółdzielni mieszkaniowej" },
            { value: "wlasnoscLokalu", label: "własność innego lokalu mieszkalnego" },
            { value: "domJednorodzinny", label: "własność domu jednorodzinnego" },
            { value: "budynekWielorodzinny", label: "własność budynku wielorodzinnego, w którym znajduje się lokal" },
            { value: "inny", label: "inny tytuł prawny" },
            { value: "bezTytulu", label: "bez tytułu prawnego, ale oczekujący na przysługujący lokal zamienny lub najem socjalny lokalu" },
          ],
        },
        { code: "tytulInny", type: "text", label: "Inny tytuł prawny — jaki", required: true, visibleWhen: { field: "tytulPrawny", equals: "inny" }, maxLength: 200 },
      ],
    },
    {
      code: "powierzchnia",
      title: "5. Powierzchnia użytkowa lokalu mieszkalnego",
      fields: [
        { code: "powierzchniaUzytkowa", type: "number", label: "Powierzchnia użytkowa lokalu [m²]", required: true, width: "half", decimals: 2, min: 1, max: 1000 },
        { code: "powierzchniaPokoiKuchni", type: "number", label: "w tym łączna powierzchnia pokoi i kuchni [m²]", required: true, width: "half", decimals: 2, min: 1, max: 1000 },
        {
          code: "powierzchniaZajmowana",
          type: "number",
          label: "Powierzchnia zajmowana przez wnioskodawcę w przypadku najmu albo podnajmu części lokalu [m²]",
          hint: "Wypełnij tylko wtedy, gdy wynajmujesz albo podnajmujesz część lokalu.",
          visibleWhen: PART_RENTED,
          width: "half",
          decimals: 2,
          min: 1,
          max: 1000,
        },
      ],
    },
    {
      code: "niepelnosprawnosc",
      title: "6. Osoby niepełnosprawne",
      fields: [
        { code: "niepelnosprawni", type: "radio", label: "Czy w gospodarstwie domowym są osoby niepełnosprawne?", required: true, options: YES_NO },
        { code: "liczbaNiepelnosprawnych", type: "number", label: "Liczba osób niepełnosprawnych", required: true, visibleWhen: DISABLED, min: 1, max: 30, width: "third" },
        {
          code: "wozekPokoj",
          type: "radio",
          label: "Czy wśród nich są osoby poruszające się na wózku inwalidzkim albo których niepełnosprawność wymaga zamieszkiwania w oddzielnym pokoju?",
          required: true,
          visibleWhen: DISABLED,
          options: YES_NO,
        },
        {
          code: "liczbaNaWozku",
          type: "number",
          label: "Liczba osób niepełnosprawnych poruszających się na wózku inwalidzkim",
          required: true,
          visibleWhen: WHEELCHAIR_OR_ROOM,
          defaultValue: "0",
          min: 0,
          max: 30,
          width: "half",
        },
        {
          code: "liczbaOddzielnyPokoj",
          type: "number",
          label: "Liczba innych osób niepełnosprawnych, których niepełnosprawność wymaga zamieszkiwania w oddzielnym pokoju",
          required: true,
          visibleWhen: WHEELCHAIR_OR_ROOM,
          defaultValue: "0",
          min: 0,
          max: 30,
          width: "half",
        },
      ],
    },
    {
      code: "wyposazenie",
      title: "7. Wyposażenie techniczne zajmowanego lokalu",
      fields: [
        { code: "centralneOgrzewanie", type: "radio", label: "Sposób ogrzewania lokalu (wyposażenie w centralne ogrzewanie)", required: true, options: INSTALLED },
        {
          code: "cieplaWoda",
          type: "radio",
          label: "Sposób przygotowywania ciepłej wody użytkowej (wyposażenie w centralną instalację ciepłej wody)",
          required: true,
          options: INSTALLED,
        },
        { code: "gazPrzewodowy", type: "radio", label: "Instalacja gazu przewodowego", required: true, options: INSTALLED },
      ],
    },
    householdSection({ title: "8. Gospodarstwo domowe", count: true }),
    {
      code: "wydatki",
      title: "9. Wydatki na lokal mieszkalny",
      fields: [
        {
          code: "wydatkiKwota",
          type: "number",
          label: "Łączna kwota wydatków na lokal mieszkalny za ostatni miesiąc [zł]",
          hint: "Za miesiąc poprzedzający miesiąc złożenia wniosku, według dokumentów (np. zawiadomienia o wysokości czynszu, rachunków).",
          required: true,
          width: "half",
          decimals: 2,
          min: 0,
        },
      ],
    },
    incomeDeclarationSection([
      {
        code: "infoKryterium",
        type: "info",
        label:
          "Kryterium dochodowe (art. 3 ust. 1 ustawy): średni miesięczny dochód na członka gospodarstwa domowego nie może przekraczać 40% przeciętnego wynagrodzenia w gospodarce narodowej w gospodarstwie jednoosobowym i 30% w gospodarstwie wieloosobowym — według komunikatu Prezesa GUS z 9 lutego 2026 r. odpowiednio 3 561,42 zł i 2 671,07 zł.",
      },
      {
        code: "infoPrzechowywanie",
        type: "info",
        label:
          "Dokumenty, na których podstawie deklarujesz dochody, musisz przechowywać przez 3 lata od dnia wydania decyzji o przyznaniu dodatku (art. 7 ust. 14 ustawy).",
      },
      penaltyDeclaration(),
    ]),
    replySection("Sposób odbioru decyzji"),
  ],
  attachments: [
    {
      code: "potwierdzenieZarzadcy",
      label: "Potwierdzenie danych wniosku przez zarządcę budynku albo inną osobę uprawnioną do pobierania należności za lokal",
      hint: "Skan wniosku albo osobnego potwierdzenia podpisanego przez zarządcę — adres, zarządca, tytuł prawny, powierzchnia, wyposażenie techniczne lokalu i wydatki za ostatni miesiąc (art. 7 ust. 1b ustawy).",
      visibleWhen: { not: HOUSE },
      required: true,
    },
    {
      code: "domJednorodzinny",
      label: "Dokumenty albo oświadczenie o powierzchni użytkowej domu, w tym łącznej powierzchni pokoi i kuchni, oraz o jego wyposażeniu technicznym",
      hint: "Art. 7 ust. 2 ustawy — dotyczy właściciela domu jednorodzinnego.",
      visibleWhen: HOUSE,
      required: true,
    },
    {
      code: "wydatkiDokumenty",
      label: "Dokumenty potwierdzające wydatki na lokal za miesiąc poprzedzający złożenie wniosku",
      hint: "Np. zawiadomienie o wysokości czynszu lub opłat, rachunki za ciepło, wodę, ścieki i odpady (art. 7 ust. 1 ustawy).",
      required: true,
      multiple: true,
    },
    incomeDocumentsAttachment(
      "Np. zaświadczenia od pracodawcy, decyzje lub odcinki ZUS/KRUS, wyroki alimentacyjne, zaświadczenia o stypendiach, zaświadczenia z urzędu pracy.",
    ),
    {
      code: "tytulPrawnyDokument",
      label: "Dokument potwierdzający tytuł prawny do lokalu",
      hint: "Nieobowiązkowo — np. umowa najmu lub podnajmu, przydział spółdzielczy, akt notarialny; urzędy często o niego proszą.",
      visibleWhen: { not: { field: "tytulPrawny", equals: "bezTytulu" } },
    },
    {
      code: "orzeczenie",
      label: "Orzeczenie o niepełnosprawności wskazujące, że osoba porusza się na wózku inwalidzkim albo wymaga zamieszkiwania w oddzielnym pokoju",
      hint: "Art. 5 ust. 3 ustawy — powiększa normatywną powierzchnię lokalu.",
      visibleWhen: WHEELCHAIR_OR_ROOM,
      required: true,
    },
    {
      code: "ryczalt",
      label: "Wniosek o ryczałt na zakup opału albo dokument stanowiący podstawę jego obliczenia",
      hint: "Gdy lokal nie ma centralnego ogrzewania, centralnej ciepłej wody lub gazu przewodowego (art. 6 ust. 7 ustawy) — jeśli urząd wymaga własnego formularza.",
      visibleWhen: NO_INSTALLATION,
    },
    {
      code: "kartaLokalu",
      label: "Informacja zarządcy lub właściciela o lokalu (np. punktowa karta lokalu)",
      hint: "Nieobowiązkowo — część urzędów wymaga jej przy lokalu wynajmowanym od prywatnego właściciela.",
      visibleWhen: PART_RENTED,
    },
    proxyWithoutFeeAttachment(
      "Skan podpisanego pełnomocnictwa (art. 33 KPA). W sprawie dodatku mieszkaniowego pełnomocnictwo jest zwolnione z opłaty skarbowej.",
    ),
    otherAttachments(),
  ],
});

// ── najem lokalu z mieszkaniowego zasobu gminy (A10 — urząd gminy, E1 — jednostka zarządzająca zasobem) ─

const KIND_PREVIOUS_TENANT: EbiCondition = { field: "rodzajNajmu", equals: "poNajemcy" };
const KIND_EXISTING_DWELLING: EbiCondition = { field: "rodzajNajmu", in: ["poNajemcy", "przywrocenie"] };
const circumstance = (value: string): EbiCondition => ({ field: "okolicznosci", includes: value });

interface IMunicipalFlatOptions {
  code: string;
  office: EEbiFormOffice;
  organ: string;
  title: string;
  description: string;
  request: string;
}

/**
 * Wniosek o najem (przydział) lokalu z mieszkaniowego zasobu gminy — art. 20–23 ustawy z 21.06.2001 r. o ochronie praw
 * lokatorów, mieszkaniowym zasobie gminy i o zmianie Kodeksu cywilnego, z deklaracją i oświadczeniami z art. 21b
 * (klauzula odpowiedzialności karnej — ust. 5). Brak wzoru ogólnopolskiego; układ jak wzory Gdańska (wniosek o najem
 * lokalu, zał. do zarządzenia nr 2341/25 — https://download.cloudgdansk.pl/gdansk-pl/d/202512267135/druk-wniosku-o-najem.pdf,
 * pobrany 22.10.2026) i Rudy Śląskiej (formularz 6.1 z załącznikami 1–3 — https://rudaslaska.bip.info.pl/plik.php?id=429635&wer=1,
 * pobrany 22.10.2026): rodzaj najmu, uzasadnienie, osoby zgłoszone do wspólnego zamieszkania, okoliczności z dokumentami,
 * deklaracja o dochodach, oświadczenie majątkowe, oświadczenie o braku tytułu do innego lokalu. Kryteria dochodowe
 * i punktowe, okres naboru i własne druki ustala uchwała rady gminy — do potwierdzenia z wydziałem.
 */
export const municipalFlatForm = ({ code, office, organ, title, description, request }: IMunicipalFlatOptions): IEbiFormDefinition => ({
  code,
  version: 1,
  title,
  category: EEbiFormCategory.Housing,
  office,
  description,
  keywords: ["mieszkanie komunalne", "lokal socjalny", "najem", "przydział lokalu", "zasób gminy", "deklaracja o dochodach"],
  organ,
  legalBasis:
    "art. 20–23 ustawy z dnia 21 czerwca 2001 r. o ochronie praw lokatorów, mieszkaniowym zasobie gminy i o zmianie Kodeksu cywilnego (w tym art. 21b — deklaracja o dochodach i oświadczenia); uchwała rady gminy w sprawie zasad wynajmowania lokali wchodzących w skład mieszkaniowego zasobu gminy (art. 21 ust. 1 pkt 2 i ust. 3)",
  request,
  feeLabel: "bez opłaty",
  fee: "Bez opłaty. Pełnomocnictwo — 17 zł opłaty skarbowej.",
  sections: [
    applicantSection(),
    representationSection(),
    {
      code: "przedmiot",
      title: "Przedmiot wniosku",
      fields: [
        {
          code: "rodzajNajmu",
          type: "radio",
          label: "Wnoszę o",
          required: true,
          options: [
            { value: "mieszkalny", label: "najem lokalu mieszkalnego" },
            { value: "socjalny", label: "najem socjalny lokalu" },
            { value: "zamienny", label: "najem lokalu zamiennego" },
            { value: "poNajemcy", label: "najem lokalu, który zajmuję, po wyprowadzeniu się lub śmierci dotychczasowego najemcy" },
            { value: "przywrocenie", label: "przywrócenie tytułu prawnego do zajmowanego lokalu po spłacie zaległości" },
          ],
        },
        { code: "dotychczasowyNajemca", type: "text", label: "Imię i nazwisko dotychczasowego najemcy", required: true, visibleWhen: KIND_PREVIOUS_TENANT, maxLength: 200 },
        { code: "adresLokalu", type: "text", label: "Adres lokalu, którego dotyczy wniosek", required: true, visibleWhen: KIND_EXISTING_DWELLING, maxLength: 300 },
        {
          code: "lokalDoRemontu",
          type: "checkbox",
          label: "Wyrażam zgodę na przyznanie lokalu do remontu na własny koszt",
          visibleWhen: { field: "rodzajNajmu", in: ["mieszkalny", "socjalny", "zamienny"] },
        },
        {
          code: "uzasadnienie",
          type: "textarea",
          label: "Uzasadnienie wniosku",
          hint: "Opisz obecne warunki mieszkaniowe i sytuację rodziny (np. gdzie i na jakiej podstawie mieszkasz, ile osób zajmuje lokal, jaka jest jego powierzchnia).",
          required: true,
          maxLength: 5000,
        },
        {
          code: "infoUchwala",
          type: "info",
          label:
            "Kryteria (dochód, warunki zamieszkiwania), pierwszeństwo i tryb rozpatrywania wniosków określa uchwała rady gminy w sprawie zasad wynajmowania lokali z mieszkaniowego zasobu gminy.",
        },
      ],
    },
    householdSection({
      title: "Osoby zgłoszone do wspólnego zamieszkania",
      membersLabel: "Osoby ubiegające się o najem wspólnie z wnioskodawcą",
      adultsQuestion: true,
    }),
    {
      code: "okolicznosciSekcja",
      title: "Szczególne okoliczności",
      fields: [
        {
          code: "okolicznosci",
          type: "checkboxes",
          label: "Zaznacz, jeśli dotyczy Ciebie lub osób zgłoszonych we wniosku",
          hint: "Do każdej zaznaczonej okoliczności dołączysz dokument, który ją potwierdza.",
          options: [
            { value: "niepelnosprawnosc", label: "w gospodarstwie domowym jest osoba z orzeczoną niepełnosprawnością" },
            { value: "eksmisja", label: "zapadł wyrok eksmisyjny dotyczący zajmowanego lokalu" },
            { value: "piecza", label: "opuszczam pieczę zastępczą (rodzinę zastępczą albo placówkę)" },
            { value: "przemoc", label: "doznaję przemocy domowej (np. prowadzona jest procedura „Niebieskie Karty”)" },
            { value: "rozwod", label: "jestem po rozwodzie lub w separacji albo mój małżonek mieszka osobno" },
            { value: "zgon", label: "zmarł mój małżonek albo dotychczasowy najemca lokalu" },
            { value: "najemObecny", label: "zajmuję obecnie lokal na podstawie umowy najmu lub podnajmu" },
          ],
        },
      ],
    },
    ...tenantDeclarationSections(),
    replySection(),
  ],
  attachments: [
    incomeDocumentsAttachment(
      "Np. zaświadczenia od pracodawcy, decyzje ZUS lub ośrodka pomocy społecznej, zaświadczenie z urzędu pracy, PIT. Gmina może też wezwać do przedstawienia zaświadczenia naczelnika urzędu skarbowego (art. 21b ust. 1a).",
    ),
    otherAdultsAttachment(),
    {
      code: "potwierdzenieZarzadcy",
      label: "Potwierdzenie zarządcy budynku o warunkach zamieszkiwania (powierzchnia lokalu, liczba zamieszkałych osób)",
      hint: "Nieobowiązkowo — wiele urzędów prosi o nie na własnym druku.",
    },
    {
      code: "orzeczenieNiepelnosprawnosci",
      label: "Orzeczenie o niepełnosprawności",
      visibleWhen: circumstance("niepelnosprawnosc"),
      required: true,
    },
    { code: "wyrokEksmisyjny", label: "Wyrok eksmisyjny", visibleWhen: circumstance("eksmisja"), required: true },
    { code: "pieczaZastepcza", label: "Zaświadczenie o pobycie w pieczy zastępczej", visibleWhen: circumstance("piecza"), required: true },
    {
      code: "przemocDokumenty",
      label: "Dokument potwierdzający przemoc domową (np. zaświadczenie o prowadzeniu procedury „Niebieskie Karty”)",
      visibleWhen: circumstance("przemoc"),
      required: true,
    },
    {
      code: "stanCywilny",
      label: "Wyrok rozwodowy, orzeczenie o separacji albo inne dokumenty stanu cywilnego",
      visibleWhen: circumstance("rozwod"),
      required: true,
    },
    { code: "aktZgonu", label: "Odpis aktu zgonu", visibleWhen: circumstance("zgon"), required: true },
    {
      code: "umowaNajmu",
      label: "Umowa najmu lub podnajmu obecnie zajmowanego lokalu",
      visibleWhen: circumstance("najemObecny"),
      required: true,
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
});
